import React from 'react'
import { HEADER_FIELD_NAMES, HEADER_INPUT_STYLE } from '../constants'
import { Autocomplete, TextField } from '@mui/material'
import { getColumnType } from '../../../../../utils/columnType'

const locationOptions = [
  { id: '1', label: 'A棟' },
  { id: '2', label: 'B棟' },
  { id: '3', label: 'C棟' },
  { id: '4', label: 'E棟' },
  { id: '5', label: '中央BOX' },
  { id: '6', label: 'ジャバラ' },
  { id: '7', label: '果実BOX前' },
  { id: '8', label: '★A棟' },
  { id: '9', label: '★B棟' },
  { id: 'A', label: '★C棟' },
  { id: 'B', label: '★D棟' },
  { id: 'C', label: '★E棟' },
  { id: 'D', label: '★ジャバラ' },
  { id: 'E', label: 'A棟北' },
  { id: 'G', label: 'Ｆ棟' },
  { id: 'H', label: '名古屋西' },
  { id: 'I', label: 'Ａ南' },
  { id: 'J', label: '玉川' },
  { id: 'K', label: '稲沢' },
  { id: 'L', label: '津島' },
  { id: 'M', label: '加工' },
  { id: 'N', label: '犬山' },
  { id: 'O', label: '瀬戸' }
]

const HEADER_LEGEND_ITEMS = {
  torioiki: [
    {
      description: '受注通りに荷割されている状態',
      color: '#808080'
    },
    {
      description: '受注数量を超えて荷割されている状態',
      color: '#0000FF'
    }
  ],
  basho: [
    {
      description: '受注数量に不足している状態',
      color: '#FFC0CB'
    },
    {
      description: 'サイズ・等級（グレード）が異なる状態',
      color: '#FFFF00'
    }
  ],
  edaban: [
    {
      description: 'AI提案による新規顧客の追加（注文なし）',
      color: '#ADD8E6'
    }
  ]
}

const HeaderLegend = ({ fieldKey, legendWidth }) => {
  const items = HEADER_LEGEND_ITEMS[fieldKey]
  const includesTextLegend = fieldKey === 'edaban'

  if (!items && !includesTextLegend) return null

  return (
    <div
      className='header-field-legend'
      style={legendWidth ? { width: `${legendWidth}px` } : undefined}
    >
      {items?.map(({ description, color }) => (
        <div className='header-field-legend__item' key={`${fieldKey}-${color}`}>
          <span
            aria-hidden='true'
            className='header-field-legend__swatch'
            style={{ backgroundColor: color }}
          ></span>
          <span className='header-field-legend__text'>
            <span>{description}</span>
          </span>
        </div>
      ))}
      {includesTextLegend && (
        <div className='header-field-legend__item header-field-legend__item--text'>
          <span
            className='header-field-legend__text header-field-legend__text--inline'
            style={{ color: '#4ca79f' }}
          >
            <span>通常文字：AI提案値</span>
            <span aria-hidden='true'>／</span>
            <strong>太字：ユーザー編集値</strong>
          </span>
        </div>
      )}
    </div>
  )
}

const BashoAutocomplete = ({
  defaultValue,
  customerCode,
  fieldKey,
  handleHeaderFieldBlur,
  disabled = false,
  selectedBg
}) => {
  const currentOption =
    locationOptions.find((o) => o.label === defaultValue) ?? null

  return (
    <Autocomplete
      options={locationOptions}
      openOnFocus={!disabled}
      value={currentOption}
      disabled={disabled}
      getOptionLabel={(option) => option?.label ?? ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      onChange={(_, newValue) => {
        if (disabled) return
        const customEvent = {
          target: { value: newValue ? newValue.label : '' }
        }
        handleHeaderFieldBlur(customEvent, customerCode, fieldKey)
      }}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.id}
          className='text-sm border-b-2 border-gray-400 px-2 py-4 cursor-pointer'
        >
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder=''
          inputProps={{
            ...params.inputProps,
            readOnly: disabled
          }}
          sx={{
            '& .MuiInputBase-root': {
              height: 40,
              backgroundColor: selectedBg ?? '#fff'
            },
            '& .MuiInputBase-input': { padding: 0, fontSize: 14 },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiOutlinedInput-root': { paddingRight: '20px !important' }
          }}
        />
      )}
      clearIcon={null}
      sx={{ height: 40, border: 'none', backgroundColor: selectedBg ?? '#fff' }}
    />
  )
}

const HeaderFieldRow = ({
  fieldKey,
  label,
  defaultOverride,
  fixedBaseColumnCount,
  fixedBaseLeft,
  legendWidth,
  buyerLeft,
  customerColumns,
  columnManager,
  handleHeaderFieldKeyDown,
  handleHeaderFieldKeyUp,
  handleHeaderFieldBlur,
  handleHeaderFieldToggle,
  isDisabled,
  selectedItems,
  orders,
  hasOriginalOrderQuantity,
  hiddenBefore = [],
  hiddenAfter = [],
  onUnhide,
  onContextMenu,
  isSelected = false,
  onSelect,
  rowRef,
  stickyTop = 0,
  showDanwareLabel = false
}) => {
  const selectedBg = isSelected ? '#bfe3ff' : undefined
  const stickyCellStyle = {
    top: `${stickyTop}px`,
    backgroundColor: selectedBg
  }

  return (
    <tr
      ref={rowRef}
      className={showDanwareLabel ? 'danware-label-row' : undefined}
    >
      <th
        className='row-number sticky-col header-field-cell text-[10px] group'
        style={{
          ...stickyCellStyle,
          left: 0,
          zIndex: 110,
          cursor: isDisabled ? 'default' : 'pointer',
          userSelect: 'none'
        }}
        onClick={(e) => {
          e.stopPropagation()
          if (isDisabled) return
          if (onSelect) {
            onSelect(e)
          }
        }}
        onContextMenu={isDisabled ? undefined : onContextMenu}
      >
        {showDanwareLabel && (
          <span
            className='danware-header-label'
            title='下記の数字にカーソルあわす'
          >
            <span>段割</span>
            <span>れを</span>
            <span>作る</span>
          </span>
        )}
        {hiddenBefore.length > 0 && (
          <div
            className='absolute top-0 left-0 right-0 h-2 flex items-center justify-center bg-blue-100 hover:bg-blue-300 transition-colors z-10'
            onClick={(e) => {
              e.stopPropagation()
              if (isDisabled) return
              hiddenBefore.forEach((k) => onUnhide(k))
            }}
            title='隠れている行を表示'
          >
            <svg
              width='8'
              height='8'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='4'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M7 11l5-5 5 5M7 18l5-5 5 5' />
            </svg>
          </div>
        )}

        {hiddenAfter.length > 0 && (
          <div
            className='absolute bottom-0 left-0 right-0 h-2 flex items-center justify-center bg-blue-100 hover:bg-blue-300 transition-colors z-10'
            onClick={(e) => {
              e.stopPropagation()
              if (isDisabled) return
              hiddenAfter.forEach((k) => onUnhide(k))
            }}
            title='隠れている行を表示'
          >
            <svg
              width='8'
              height='8'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='4'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M7 13l5 5 5-5M7 6l5 5 5-5' />
            </svg>
          </div>
        )}
      </th>
      {fixedBaseColumnCount > 1 && (
        <th
          className={`group-header sticky-col header-field-cell header-field-fixed-spacer header-field-fixed-spacer--${fieldKey}`}
          colSpan={fixedBaseColumnCount - 1}
          style={{
            ...stickyCellStyle,
            left: `${fixedBaseLeft}px`,
            zIndex: 110
          }}
        >
          <HeaderLegend fieldKey={fieldKey} legendWidth={legendWidth} />
        </th>
      )}
      <th
        className='group-header sticky-col sticky-shadow header-field-cell'
        style={{ ...stickyCellStyle, left: `${buyerLeft}px`, zIndex: 110 }}
      >
        {label}
      </th>
      {customerColumns.map((col) => {
        const overrideValue = defaultOverride
          ? defaultOverride(col, columnManager, selectedItems)
          : null
        const defaultValue =
          overrideValue !== null
            ? overrideValue
            : columnManager.getCustomerHeaderField(col.customerCode, fieldKey)

        const isTorioikiField = fieldKey === 'torioiki'
        const displayValue =
          defaultValue === '●' ||
          defaultValue === '＋1日' ||
          defaultValue === '＋2日'
            ? defaultValue
            : ''

        const thStyle = {
          ...stickyCellStyle,
          padding: '2px',
          cursor:
            isTorioikiField && !isDisabled
              ? 'pointer'
              : isTorioikiField
                ? 'not-allowed'
                : undefined
        }

        return (
          <th
            key={`${fieldKey}-${col.id}`}
            className='group-header editable-header header-field-cell'
            style={thStyle}
          >
            {label === HEADER_FIELD_NAMES.basho ? (
              <BashoAutocomplete
                key={`basho-${col.customerCode}-${defaultValue}`}
                defaultValue={defaultValue}
                customerCode={col.customerCode}
                fieldKey={fieldKey}
                handleHeaderFieldBlur={handleHeaderFieldBlur}
                disabled={isDisabled}
                selectedBg={selectedBg}
              />
            ) : isTorioikiField ? (
              <div
                key={`${fieldKey}-${col.customerCode}-${displayValue}`}
                role='button'
                tabIndex={0}
                aria-pressed={displayValue === '●'}
                onClick={() =>
                  !isDisabled &&
                  handleHeaderFieldToggle(col.customerCode, fieldKey)
                }
                onKeyDown={(e) => {
                  if (isDisabled) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleHeaderFieldToggle(col.customerCode, fieldKey)
                  }
                }}
                style={{
                  ...HEADER_INPUT_STYLE,
                  background: selectedBg ?? '#fff',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 40,
                  userSelect: 'none'
                }}
              >
                {displayValue}
              </div>
            ) : (
              <input
                key={`${fieldKey}-${col.customerCode}-${defaultValue}`}
                type='text'
                className='header-input'
                defaultValue={defaultValue}
                onKeyDown={(e) =>
                  !isDisabled &&
                  handleHeaderFieldKeyDown(e, col.customerCode, fieldKey)
                }
                onKeyUp={(e) =>
                  !isDisabled &&
                  handleHeaderFieldKeyUp(e, col.customerCode, fieldKey)
                }
                style={{
                  ...HEADER_INPUT_STYLE,
                  background: selectedBg ?? '#fff'
                }}
                onBlur={(e) =>
                  !isDisabled &&
                  handleHeaderFieldBlur(e, col.customerCode, fieldKey)
                }
                disabled={
                  isDisabled ||
                  (fieldKey === 'edaban' &&
                    getColumnType(
                      col.customerCode,
                      orders,
                      hasOriginalOrderQuantity
                    ) !== 'deletable')
                }
              />
            )}
          </th>
        )
      })}
      <th
        aria-hidden='true'
        className='group-header add-column-spacer header-field-cell'
        style={{ ...stickyCellStyle, width: '80px', minWidth: '80px' }}
      ></th>
    </tr>
  )
}

export default HeaderFieldRow
